import moment from "moment";
import { useEffect, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";

import ContainerAnimation from "components/ContainerAnimation";
import LoadingScreen from "components/LoadingScreen";
import PaymentInfo from "components/PaymentInfo";
import QuoteStatusInfo from "components/QuoteStatusInfo";
import { useAuth } from "providers/Auth";
import { api } from "services";
import {
  PaymentMethod,
  RoleDescription,
  quoteStatusColor,
} from "types/enums";
import { Quote } from "types/quotes";
import { addBusinessDays } from "utils/businessDays";
import { downloadCnabForDay, CnabDayGroup } from "utils/cnabExport";
import { formatPrice } from "utils/formats";
import { REPASSE_BUSINESS_DAYS } from "utils/repasse";
import { toast } from "utils/toast";

import * as S from "./styles";

interface ILocationState {
  date: Date;
  totalValue: number;
  companyId: number;
  professionalId: number;
}

const ScheduleDetails = () => {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExportingCnab, setIsExportingCnab] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { userRole } = useAuth();

  if (!location.state) {
    return <Navigate to="/" />;
  }

  const state = location.state as ILocationState;
  const eventDate = moment(state.date).add(1, "d");

  useEffect(() => {
    async function loadQuotes() {
      try {
        const { data } = await api.get("/Payment/QuoteForecastingDetails", {
          params: {
            date: eventDate.format("YYYY-MM-DD"),
            companyId: state.companyId === -1 ? undefined : state.companyId,
            professionalId:
              state.professionalId === -1 ? undefined : state.professionalId,
          },
        });
        setQuotes(data);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    }
    loadQuotes();
  }, []);

  if (isLoading) {
    return (
      <ContainerAnimation className="content">
        <LoadingScreen />
      </ContainerAnimation>
    );
  }

  const totalValue = state.totalValue;
  const paidQuotes = quotes.filter((q) => q.payment?.status === 1); // Paid
  const pendingQuotes = quotes.filter((q) => q.payment?.status !== 1);

  // Repasse segue a regra D+2: pagamento neste dia → crédito em 2 dias úteis
  const repasseCreditDate = addBusinessDays(
    eventDate.format("YYYY-MM-DD"),
    REPASSE_BUSINESS_DAYS
  );

  const handleExportCnab = async () => {
    setIsExportingCnab(true);
    try {
      const groups: Record<number, CnabDayGroup> = {};
      for (const quote of quotes) {
        const value = quote.payment?.compensationValue ?? 0;
        const companyId = quote.company?.id;
        if (value <= 0 || !companyId) continue;
        groups[companyId] = groups[companyId] ?? {
          companyId,
          companyName: quote.company.name,
          items: [],
        };
        groups[companyId].items.push({
          quoteId: quote.id,
          professionalId: quote.professional?.id,
          professionalName: quote.professional?.name ?? "",
          value,
        });
      }

      const companyGroups = Object.values(groups);
      if (companyGroups.length === 0) {
        toast.fire({
          icon: "warning",
          title: "Nenhum orçamento com valor de repasse neste dia.",
        });
        return;
      }

      // Um único arquivo de remessa com todos os repasses do dia
      await downloadCnabForDay({
        creditDate: repasseCreditDate.format("YYYY-MM-DD"),
        groups: companyGroups,
      });
    } catch (err) {
      console.error("[CNAB240] ERRO:", err);
      toast.fire({
        icon: "error",
        title: "Erro ao gerar o CNAB 240. Tente novamente.",
      });
    } finally {
      setIsExportingCnab(false);
    }
  };

  return (
    <ContainerAnimation className="content">
      {/* ── Header ── */}
      <S.PageHeader>
        <div className="container">
          <S.Breadcrumb aria-label="Navegação">
            <Link to="/agenda">Calendário</Link>
            <span aria-hidden="true">›</span>
            <span aria-current="page">
              {eventDate.format("DD/MM/YYYY")}
            </span>
          </S.Breadcrumb>

          <S.HeaderRow>
            <S.PageTitle>
              <S.BackButton
                onClick={() => navigate(-1)}
                aria-label="Voltar para o calendário"
                title="Voltar"
              >
                <i className="uil uil-arrow-left" aria-hidden="true" />
              </S.BackButton>
              Previsão de Pagamento
              <S.DateBadge>{eventDate.format("DD/MM/YYYY")}</S.DateBadge>
            </S.PageTitle>

            {userRole === RoleDescription.Admin && quotes.length > 0 && (
              <div>
                <S.CnabButton
                  onClick={handleExportCnab}
                  disabled={isExportingCnab}
                  aria-label="Baixar arquivo CNAB 240 com os repasses do dia"
                >
                  {isExportingCnab ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                      Gerando…
                    </>
                  ) : (
                    <>
                      <i className="uil uil-file-download" aria-hidden="true" />
                      Baixar CNAB 240
                    </>
                  )}
                </S.CnabButton>
                <S.CnabHint>
                  Crédito em {repasseCreditDate.format("DD/MM/YYYY")} (D+
                  {REPASSE_BUSINESS_DAYS} úteis)
                </S.CnabHint>
              </div>
            )}
          </S.HeaderRow>
        </div>
      </S.PageHeader>

      <div className="container pt-3">
        {/* ── Summary bar ── */}
        {quotes.length > 0 && (
          <S.SummaryBar aria-label="Resumo do dia">
            <S.SummaryItem>
              <div className="s-label">Total do dia</div>
              <div className="s-value">{formatPrice(totalValue)}</div>
            </S.SummaryItem>
            <S.SummaryItem>
              <div className="s-label">Orçamentos</div>
              <div className="s-value" style={{ color: "#111827" }}>
                {quotes.length}
              </div>
            </S.SummaryItem>
            {paidQuotes.length > 0 && (
              <S.SummaryItem>
                <div className="s-label">Pagos</div>
                <div className="s-value" style={{ color: "#15803d" }}>
                  {paidQuotes.length}
                </div>
              </S.SummaryItem>
            )}
            {pendingQuotes.length > 0 && (
              <S.SummaryItem>
                <div className="s-label">Pendentes</div>
                <div className="s-value" style={{ color: "#b45309" }}>
                  {pendingQuotes.length}
                </div>
              </S.SummaryItem>
            )}
          </S.SummaryBar>
        )}

        {/* ── Empty state ── */}
        {quotes.length === 0 && (
          <S.EmptyState role="status">
            <i className="uil uil-calendar-slash" aria-hidden="true" />
            <p>Nenhum orçamento encontrado para esta data</p>
          </S.EmptyState>
        )}

        {/* ── Quote cards ── */}
        {quotes.map((quote) => {
          const isFinancing = quote.payment?.method === PaymentMethod.Financing;
          const statusColorVar = `var(--${quoteStatusColor.get(quote.quoteStatus)})`;

          return (
            <S.Card
              key={quote.id}
              style={{ "--status-color": statusColorVar } as React.CSSProperties}
              aria-label={`Orçamento #${quote.id} — ${quote.patient.name}`}
            >
              {/* Header */}
              <S.CardHeader>
                <S.QuoteId>#{quote.id}</S.QuoteId>

                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>{quote.patient.name}</Tooltip>}
                >
                  <S.PatientName>{quote.patient.name}</S.PatientName>
                </OverlayTrigger>

                <QuoteStatusInfo
                  quoteStatus={quote.quoteStatus}
                  paymentMethod={quote.payment.method}
                />

                <S.DetailsButton
                  as={Link}
                  to="/orcamentos-profissional/orcamento-detalhes"
                  state={quote.id}
                  aria-label={`Ver detalhes do orçamento #${quote.id}`}
                >
                  <i className="uil uil-arrow-right" aria-hidden="true" />
                  Detalhes
                </S.DetailsButton>
              </S.CardHeader>

              {/* Body */}
              <S.CardBody aria-label="Informações do orçamento">
                <S.MetaItem>
                  <S.MetaLabel>Profissional</S.MetaLabel>
                  {quote.professional?.name ? (
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>{quote.professional.name}</Tooltip>}
                    >
                      <S.MetaValue>{quote.professional.name}</S.MetaValue>
                    </OverlayTrigger>
                  ) : (
                    <S.MetaValueMuted>Não informado</S.MetaValueMuted>
                  )}
                  {quote.company?.name && (
                    <S.MetaSub>{quote.company.name}</S.MetaSub>
                  )}
                </S.MetaItem>

                <S.MetaItem>
                  <S.MetaLabel>Procedimento</S.MetaLabel>
                  <S.MetaValue>
                    {moment(quote.date).format("DD/MM/YYYY")}
                  </S.MetaValue>
                </S.MetaItem>

                <S.MetaItem>
                  <S.MetaLabel>Limite</S.MetaLabel>
                  {quote.hasLimitDateAlert ? (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>Orçamento está prestes a expirar</Tooltip>
                      }
                    >
                      <S.MetaValueDanger>
                        <i
                          className="uil uil-exclamation-triangle"
                          aria-hidden="true"
                        />
                        {moment(quote.limitDate).format("DD/MM/YY")}
                      </S.MetaValueDanger>
                    </OverlayTrigger>
                  ) : (
                    <S.MetaValue>
                      {moment(quote.limitDate).format("DD/MM/YYYY")}
                    </S.MetaValue>
                  )}
                </S.MetaItem>

                <S.MetaItem>
                  <S.MetaLabel>
                    Valor <PaymentInfo payment={quote.payment} />
                  </S.MetaLabel>
                  <S.MetaValuePrimary>
                    {formatPrice(quote.payment.value)}
                  </S.MetaValuePrimary>
                </S.MetaItem>

                {userRole === RoleDescription.Admin &&
                  !!quote.payment.orderId && (
                    <S.MetaItem>
                      <S.MetaLabel>Pagamento</S.MetaLabel>
                      <S.MetaValue>#{quote.payment.orderId}</S.MetaValue>
                    </S.MetaItem>
                  )}
              </S.CardBody>

              {/* Footer */}
              <S.CardFooter>
                <S.PaymentBadge $dark={isFinancing}>
                  <i
                    className={`uil ${
                      isFinancing
                        ? "uil-files-landscapes-alt"
                        : "uil-credit-card"
                    }`}
                    aria-hidden="true"
                  />
                  {isFinancing ? "Financiamento" : "Parcelamento"}
                </S.PaymentBadge>

                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      {quote.hasTerm ? "Termo assinado" : "Termo não assinado"}
                    </Tooltip>
                  }
                >
                  <S.DocIndicator
                    $active={quote.hasTerm}
                    aria-label={
                      quote.hasTerm ? "Termo assinado" : "Termo não assinado"
                    }
                  >
                    <i className="uil uil-file-edit-alt" aria-hidden="true" />
                    Termo
                  </S.DocIndicator>
                </OverlayTrigger>

                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      {quote.hasAllDocumention
                        ? "Documentação aprovada"
                        : "Documentos pendentes"}
                    </Tooltip>
                  }
                >
                  <S.DocIndicator
                    $active={quote.hasAllDocumention}
                    aria-label={
                      quote.hasAllDocumention
                        ? "Documentação aprovada"
                        : "Documentos pendentes"
                    }
                  >
                    <i className="uil uil-file-upload" aria-hidden="true" />
                    Docs
                  </S.DocIndicator>
                </OverlayTrigger>

                {quote.hasCheckList && (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        {quote.doneCheckList
                          ? "Checklist completo"
                          : "Checklist incompleto"}
                      </Tooltip>
                    }
                  >
                    <S.DocIndicator
                      $active={quote.doneCheckList}
                      aria-label={
                        quote.doneCheckList
                          ? "Checklist completo"
                          : "Checklist incompleto"
                      }
                    >
                      <i className="uil uil-check-circle" aria-hidden="true" />
                      Checklist
                    </S.DocIndicator>
                  </OverlayTrigger>
                )}
              </S.CardFooter>
            </S.Card>
          );
        })}
      </div>
    </ContainerAnimation>
  );
};

export default ScheduleDetails;
